import React, { memo, useEffect, useState } from 'react'
import {
  DriverStatusType,
  ICustomerContact,
  IDriver,
  ILoadSettings,
} from 'apps/fleet-planner/types'
import { Toast, useToastState } from 'transfix-ui'
import { DriverSettingsModal } from '../../driver-led-status/DriverSettingsModal'
import { SidebarItem } from '../sidebar/SidebarItem'
import { DriverStatusRow } from './DriverStatusRow'
import { SubscriberStatusRow } from './SubscriberStatusRow'
import { SubscribersSettingsModal } from './SubscribersSettingModal'
import { UnassignedDriverStatusRow } from './UnassignedDriverStatusRow'

export interface ILoadSubscribers {
  drivers?: IDriver[]
  subscribers?: ICustomerContact[]
  loadSettings?: ILoadSettings
  isExternal?: boolean
  customerContact?: ICustomerContact
  /**
   * @default true
   */
  showDriverStatus?: boolean
  setInlineEditField?: (name?: string) => void
  onUnassignedDriverClick?: (name?: string, elementId?: string) => void
}

export const LoadSubscribers = memo<ILoadSubscribers>(
  ({
    drivers: driversProp = [],
    subscribers: subscribersProp = [],
    loadSettings,
    isExternal = false,
    customerContact,
    onUnassignedDriverClick,
    showDriverStatus = true,
  }) => {
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const [driversList, setDriversList] = useState<IDriver[]>(driversProp)
    const [isDriverModalOpen, setIsDriverModalOpen] = useState<boolean>(false)
    const [isSubscriberModalOpen, setIsSubscriberModalOpen] = useState<boolean>(false)
    const driversAssigned =
      driversList.length > 0 &&
      driversList.some(driver => driver.status === DriverStatusType.active)

    // Update state when props update
    useEffect(() => {
      if (driversProp) {
        setDriversList(driversProp)
      }
    }, [driversProp])

    const clickDriverUpdatesHandler = () => {
      setIsDriverModalOpen(true)
    }

    const cancelDriverUpdatesHandler = () => {
      setIsDriverModalOpen(false)
    }

    const clickSubscriberUpdatesHandler = () => {
      setIsSubscriberModalOpen(true)
    }

    const cancelSubscriberUpdatesHandler = () => {
      setIsSubscriberModalOpen(false)
    }

    return (
      <div>
        <DriverSettingsModal
          isOpen={isDriverModalOpen}
          drivers={driversList}
          onClose={cancelDriverUpdatesHandler}
          onSuccess={openToast}
        />
        <SubscribersSettingsModal
          isOpen={isSubscriberModalOpen}
          onClose={cancelSubscriberUpdatesHandler}
          subscribers={subscribersProp}
          loadSettings={loadSettings}
          onSuccess={openToast}
          isExternal={isExternal}
          customerContact={customerContact}
        />
        {showDriverStatus && driversAssigned && (
          <SidebarItem>
            <DriverStatusRow
              drivers={driversList}
              onClick={clickDriverUpdatesHandler}
              onCancel={cancelDriverUpdatesHandler}
            />
          </SidebarItem>
        )}
        {showDriverStatus && !driversAssigned && (
          <SidebarItem>
            <UnassignedDriverStatusRow onClick={onUnassignedDriverClick} />
          </SidebarItem>
        )}
        <SidebarItem>
          <SubscriberStatusRow
            subscribers={subscribersProp}
            settings={loadSettings}
            onClick={clickSubscriberUpdatesHandler}
            onCancel={cancelSubscriberUpdatesHandler}
          />
        </SidebarItem>
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </div>
    )
  }
)
